/**
 * UserManagement Component
 * 
 * Admin-only interface for managing company users.
 * Allows admins to:
 * - View all users in the company
 * - Change user roles
 * - Activate/deactivate users
 * - Add new users by email (invitation placeholder)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  AdminPanelSettings as AdminIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useCompanyMembership } from '../../contexts/CompanyMembershipContext';
import { usePermissions } from '../../hooks/usePermissions';
import { AVAILABLE_ROLES } from '../../types/permissions';
import { db } from '../../firebase/config';
import { collection, getDocs, doc, updateDoc, setDoc, query, where } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

export default function UserManagement() {
  const { companyId, refreshMembership } = useCompanyMembership();
  const { canManageUsers, isAdmin } = usePermissions();
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    role: 'tech',
    isActive: true,
  });
  const [addFormData, setAddFormData] = useState({
    email: '',
    name: '',
    role: 'tech',
  });

  // Load users from company
  useEffect(() => {
    async function loadUsers() {
      if (!companyId || !canManageUsers) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const usersRef = collection(db, 'companies', companyId, 'users');
        const usersSnapshot = await getDocs(usersRef);
        
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          uid: doc.id, // userUid is the document ID
          ...doc.data(),
        }));

        setUsers(usersData.sort((a, b) => {
          // Sort: admins first, then by name
          if (a.role === 'admin' && b.role !== 'admin') return -1;
          if (a.role !== 'admin' && b.role === 'admin') return 1;
          return (a.name || a.email || '').localeCompare(b.name || b.email || '');
        }));
      } catch (err) {
        console.error('Error loading users:', err);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    loadUsers();
  }, [companyId, canManageUsers]);

  // Don't render if user doesn't have permission
  if (!canManageUsers || !isAdmin) {
    return (
      <Alert severity="warning">
        You don't have permission to manage users. Only administrators can access this section.
      </Alert>
    );
  }

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      role: user.role || 'tech',
      isActive: user.isActive !== false, // Default to true if undefined
    });
    setEditDialogOpen(true);
    setError(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setError(null);
  };

  const handleSaveUser = async () => {
    if (!selectedUser || !companyId) return;

    try {
      setError(null);
      
      const userRef = doc(db, 'companies', companyId, 'users', selectedUser.uid);
      await updateDoc(userRef, {
        role: editFormData.role,
        isActive: editFormData.isActive,
        updatedAt: new Date().toISOString(),
      });

      // Update local state
      setUsers(prev => prev.map(u => 
        u.uid === selectedUser.uid 
          ? { ...u, role: editFormData.role, isActive: editFormData.isActive }
          : u
      ));

      // If editing own role, refresh membership
      if (selectedUser.uid === currentUser.uid) {
        await refreshMembership();
      }

      setSuccess(`User ${selectedUser.name || selectedUser.email} updated successfully.`);
      setTimeout(() => setSuccess(null), 3000);
      handleCloseEditDialog();
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message || 'Failed to update user. Please try again.');
    }
  };

  const handleOpenAddDialog = () => {
    setAddFormData({
      email: '',
      name: '',
      role: 'tech',
    });
    setAddDialogOpen(true);
    setError(null);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setError(null);
  };

  const handleAddUser = async () => {
    if (!companyId || !addFormData.email) {
      setError('Email is required.');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(addFormData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      setError(null);
      
      // For now, we'll create a placeholder user document
      // In the future, this should trigger an invitation flow
      // The user will be activated when they accept the invitation
      
      // Generate a temporary user ID (will be replaced when user accepts invite)
      const tempUserId = `invite_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const userRef = doc(db, 'companies', companyId, 'users', tempUserId);
      await setDoc(userRef, {
        email: addFormData.email,
        name: addFormData.name || addFormData.email.split('@')[0],
        role: addFormData.role,
        isActive: false, // Inactive until user accepts invitation
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        invitedBy: currentUser.uid,
        invitationPending: true,
      });

      // Reload users
      const usersRef = collection(db, 'companies', companyId, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        uid: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData.sort((a, b) => {
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (a.role !== 'admin' && b.role === 'admin') return 1;
        return (a.name || a.email || '').localeCompare(b.name || b.email || '');
      }));

      setSuccess(`Invitation sent to ${addFormData.email}. The user will be activated when they accept.`);
      setTimeout(() => setSuccess(null), 5000);
      handleCloseAddDialog();
    } catch (err) {
      console.error('Error adding user:', err);
      setError(err.message || 'Failed to add user. Please try again.');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'estimator': return 'info';
      case 'tech': return 'default';
      case 'office': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading users...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Team Members</Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No users found. Add your first team member to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.uid}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {user.role === 'admin' && <AdminIcon color="error" fontSize="small" />}
                      <Typography variant="body2">
                        {user.name || user.email || 'N/A'}
                      </Typography>
                      {user.uid === currentUser.uid && (
                        <Chip label="You" size="small" color="primary" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">{user.email || 'N/A'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={AVAILABLE_ROLES.find(r => r.value === user.role)?.label || user.role}
                      size="small"
                      color={getRoleColor(user.role)}
                    />
                  </TableCell>
                  <TableCell>
                    {user.isActive === false ? (
                      <Chip
                        icon={<BlockIcon />}
                        label="Inactive"
                        size="small"
                        color="default"
                      />
                    ) : user.invitationPending ? (
                      <Chip
                        label="Pending Invite"
                        size="small"
                        color="warning"
                      />
                    ) : (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Active"
                        size="small"
                        color="success"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString()
                        : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit user">
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        disabled={user.uid === currentUser.uid && user.role === 'admin'}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit User: {selectedUser?.name || selectedUser?.email}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editFormData.role}
                onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                label="Role"
                disabled={selectedUser?.uid === currentUser.uid && selectedUser?.role === 'admin'}
              >
                {AVAILABLE_ROLES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      - {role.description}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
              {selectedUser?.uid === currentUser.uid && selectedUser?.role === 'admin' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  You cannot change your own admin role.
                </Typography>
              )}
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.isActive}
                  onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })}
                  disabled={selectedUser?.uid === currentUser.uid}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Active</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inactive users cannot access the application
                  </Typography>
                </Box>
              }
            />
            {selectedUser?.uid === currentUser.uid && (
              <Typography variant="caption" color="text.secondary">
                You cannot deactivate yourself.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Alert severity="info">
              Enter the user's email address. They will receive an invitation to join your company.
              Once they accept, they'll be activated automatically.
            </Alert>

            <TextField
              label="Email Address"
              type="email"
              value={addFormData.email}
              onChange={(e) => setAddFormData({ ...addFormData, email: e.target.value })}
              fullWidth
              required
              helperText="The user will receive an invitation at this email"
            />

            <TextField
              label="Name (Optional)"
              value={addFormData.name}
              onChange={(e) => setAddFormData({ ...addFormData, name: e.target.value })}
              fullWidth
              helperText="Leave blank to use email prefix"
            />

            <FormControl fullWidth>
              <InputLabel>Initial Role</InputLabel>
              <Select
                value={addFormData.role}
                onChange={(e) => setAddFormData({ ...addFormData, role: e.target.value })}
                label="Initial Role"
              >
                {AVAILABLE_ROLES.filter(r => r.value !== 'admin').map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      - {role.description}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                New users cannot be created as admin. You can change their role after they join.
              </Typography>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button
            onClick={handleAddUser}
            variant="contained"
            disabled={!addFormData.email}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

